/**
 * LOD0 Brand Image same-app invalidation bus.
 *
 * A successful AUTHORITATIVE Brand Image mutation (SAVE / REPLACE / REMOVE) in
 * one part of the app (the Dashboard) notifies the map viewer to re-resolve
 * EXACTLY the affected persistent Identity — so a visible Brand Image never
 * stays stale after an in-session mutation, WITHOUT a page reload, WITHOUT
 * polling, WITHOUT per-frame requests, and WITHOUT optimistic client authority
 * (the viewer always re-reads the backend and lets revision/hash drive the
 * texture key).
 *
 * Transport is a plain `window` CustomEvent scoped by `identity_id`; there is
 * no shared mutable state, so it is safe across mounts and SSR (no-op without a
 * DOM). Only SUCCESSFUL mutations emit — a failed/409 mutation emits nothing
 * and therefore can never invalidate a valid current image.
 */
const EVENT = "thegrid:brand-image-mutation";

export type BrandImageMutationKind = "save" | "remove";

export interface BrandImageMutationDetail {
  identityId: string;
  kind: BrandImageMutationKind;
}

/** Announce a confirmed authoritative Brand Image mutation for one identity. */
export function emitBrandImageMutation(identityId: string, kind: BrandImageMutationKind): void {
  if (!identityId) return;
  if (typeof window === "undefined" || typeof CustomEvent === "undefined") return;
  window.dispatchEvent(new CustomEvent<BrandImageMutationDetail>(EVENT, { detail: { identityId, kind } }));
}

/** Subscribe to authoritative Brand Image mutations. Returns an unsubscribe. */
export function onBrandImageMutation(cb: (detail: BrandImageMutationDetail) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<BrandImageMutationDetail>).detail;
    if (detail && detail.identityId) cb(detail);
  };
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}
